import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
    type FormEvent,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { getOrCreateCurrentUser } from '../GetOrCreateUser';
import {
    retireClaimedClientId,
    restoreLocalAccountId,
} from '../clientIdHelper';
import api from '../../api';
import { containsProfanities } from '../../shared/utils/ContainsProfanity';

type AppUser = {
    id: string;
    name: string;
    anonymous?: boolean;
    cognitoSub?: string;
    clientId?: string;
    email?: string;
    displayName?: string;
    plan?: 'free' | 'pro' | 'team' | 'flashroom-owner';
    migratedClientId?: string;
};

type AuthUser = ReturnType<typeof useAuth>['user'];

type UserContextValue = {
    user: AppUser | null;
    loading: boolean;
    error: string | null;
    refreshUser: () => Promise<void>;
    updateDisplayName: (displayName: string) => Promise<void>;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);
const CLIENT_ID_KEY = 'flashroom_id';
const ACCOUNT_CLIENT_ID_KEY = 'flashroom-accountClientId';
const ACCOUNT_ID_KEY = 'flashroom-accountId';
const ACCOUNT_NAME_KEY = 'flashroom-accountName';
const ACCOUNT_EMAIL_KEY = 'flashroom-accountEmail';
const REMEMBERED_ACCOUNT_NAME_KEY = 'flashroom-rememberedAccountName';
const REMEMBERED_ACCOUNT_EMAIL_KEY = 'flashroom-rememberedAccountEmail';
const DISPLAY_NAME_ONBOARDING_VALUE = 'FlashRoom User';
const SESSION_WAKE_REFRESH_AFTER_MS = 10 * 60 * 1000;
const ACCOUNT_TOKEN_REFRESH_SKEW_SECONDS = 60;
const PLACEHOLDER_NAMES = new Set([
    DISPLAY_NAME_ONBOARDING_VALUE.toLowerCase(),
    'owner',
]);
const MAX_DISPLAY_NAME_LENGTH = 20;

function isPlaceholderName(name?: string) {
    const normalized = name?.trim().toLowerCase();
    return !!normalized && PLACEHOLDER_NAMES.has(normalized);
}

function needsDisplayNameOnboarding(user: AppUser | null) {
    if (!user) return false;
    return isPlaceholderName(user.displayName) || isPlaceholderName(user.name);
}

function shouldRefreshAuthUser(
    authUser?: AuthUser
) {
    if (!authUser?.access_token) return false;
    if (authUser.expired) return true;
    if (!authUser.expires_at) return false;

    return authUser.expires_at - Math.floor(Date.now() / 1000) <= ACCOUNT_TOKEN_REFRESH_SKEW_SECONDS;
}

function normalizeEmail(email: unknown): string | undefined {
    return typeof email === 'string' && email.trim()
        ? email.trim().toLowerCase()
        : undefined;
}

function getJwtPayload(
    token?: string
): Record<string, unknown> | undefined {
    const payload = token?.split('.')[1];

    if (!payload) return undefined;

    try {
        const base64 = payload
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const padded = base64.padEnd(
            Math.ceil(base64.length / 4) * 4,
            '='
        );

        return JSON.parse(atob(padded));
    } catch {
        return undefined;
    }
}

function getAuthEmail(
    authUser: AuthUser
): string | undefined {
    const profile = authUser?.profile as
        | Record<string, unknown>
        | undefined;

    const idTokenPayload = getJwtPayload(authUser?.id_token);

    return (
        normalizeEmail(profile?.email) ||
        normalizeEmail(profile?.['cognito:email']) ||
        normalizeEmail(idTokenPayload?.email) ||
        normalizeEmail(idTokenPayload?.['cognito:email'])
    );
}

function mergeUserWithAvailableEmail(
    user: AppUser,
    authUser: AuthUser
): AppUser {
    return {
        ...user,
        email:
            user.email ||
            getAuthEmail(authUser) ||
            localStorage.getItem(ACCOUNT_EMAIL_KEY)?.trim() ||
            localStorage.getItem(REMEMBERED_ACCOUNT_EMAIL_KEY)?.trim() ||
            undefined,
    };
}

function persistAccountUser(appUser: AppUser | null) {
    if (!appUser) return;

    if (appUser.clientId) {
        localStorage.setItem(ACCOUNT_CLIENT_ID_KEY, appUser.clientId);
        localStorage.setItem(CLIENT_ID_KEY, appUser.clientId);
    }

    if (appUser.id) {
        localStorage.setItem(ACCOUNT_ID_KEY, appUser.id);
    }

    const accountName = appUser.displayName || appUser.name;

    if (accountName) {
        localStorage.setItem(ACCOUNT_NAME_KEY, accountName);
        localStorage.setItem(REMEMBERED_ACCOUNT_NAME_KEY, accountName);
    }

    if (appUser.email) {
        localStorage.setItem(ACCOUNT_EMAIL_KEY, appUser.email);
        localStorage.setItem(REMEMBERED_ACCOUNT_EMAIL_KEY, appUser.email);
    }
}

export function UserProvider({
    children,
}: {
    children: ReactNode;
}) {
    const auth = useAuth();

    const [user, setUser] = useState<AppUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const silentRestoreAttemptedRef = useRef(false);
    const lastSessionWakeCheckRef = useRef(Date.now());
    const loadRequestRef = useRef(0);

    const loadAuthenticatedUser = async (
        authUser = auth.user
    ) => {
        const requestId = ++loadRequestRef.current;
        setLoading(true);
        setError(null);

        try {
            const data = await getOrCreateCurrentUser(
                auth,
                authUser?.access_token
            );
            const appUser: AppUser | null = data.user
                ? mergeUserWithAvailableEmail(data.user, authUser)
                : null;

            if (requestId !== loadRequestRef.current) return;

            setUser(appUser);

            if (appUser?.migratedClientId) {
                retireClaimedClientId(appUser.migratedClientId, appUser.clientId);
            }

            persistAccountUser(appUser);
        } catch (err) {
            if (requestId !== loadRequestRef.current) return;

            setUser(null);
            setError('Unable to load your FlashRoom account');
        } finally {
            if (requestId === loadRequestRef.current) {
                setLoading(false);
            }
        }
    };

    const trySilentRestore = async (): Promise<AuthUser | null> => {
        if (
            silentRestoreAttemptedRef.current ||
            auth.activeNavigator === 'signinSilent' ||
            !auth.user?.refresh_token
        ) {
            return null;
        }

        silentRestoreAttemptedRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const restoredUser = await auth.signinSilent();

            if (!restoredUser?.access_token) {
                return null;
            }

            return restoredUser;
        } catch (err) {
            return null;
        }
    };

    const refreshUser = async () => {
        if (auth.activeNavigator === 'signinSilent') {
            setLoading(true);
            return;
        }

        const authUserNeedsRefresh = shouldRefreshAuthUser(auth.user);
        const shouldUseCurrentAuthUser =
            auth.isAuthenticated &&
            auth.user?.access_token &&
            !authUserNeedsRefresh;

        if (shouldUseCurrentAuthUser) {
            silentRestoreAttemptedRef.current = false;
            await loadAuthenticatedUser(auth.user);
            return;
        }

        const restoredUser = await trySilentRestore();

        if (restoredUser?.access_token) {
            await loadAuthenticatedUser(restoredUser);
            return;
        }

        if (!auth.isAuthenticated || !auth.user?.access_token || authUserNeedsRefresh) {
            restoreLocalAccountId();
            setUser(null);
            setError(authUserNeedsRefresh ? 'Unable to load your FlashRoom account' : null);
            setLoading(false);
            return;
        }
    };

    const updateDisplayName = async (displayName: string) => {
        const trimmed = displayName.trim();
        const authUser =
            shouldRefreshAuthUser(auth.user)
                ? await trySilentRestore()
                : auth.user;
        const token = authUser?.access_token;

        if (!token) {
            throw new Error('Missing Cognito access token');
        }

        const response = await api.patch(
            '/users/me/display-name',
            { displayName: trimmed },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        const updatedUser = response.data?.user as AppUser | undefined;
        if (!updatedUser) {
            throw new Error('No user data returned from server');
        }

        const appUser = mergeUserWithAvailableEmail(updatedUser, authUser);
        setUser(appUser);
        persistAccountUser(appUser);
        void refreshUser();
    };

    useEffect(() => {
        if (auth.isLoading) {
            setLoading(true);
            return;
        }

        void refreshUser();
    }, [
        auth.isLoading,
        auth.isAuthenticated,
        auth.activeNavigator,
        auth.user?.access_token,
        auth.user?.expires_at,
        auth.user?.expired,
        auth.user?.id_token,
        auth.user?.profile?.email,
    ]);

    useEffect(() => {
        if (auth.isLoading) return;

        const maybeRefreshUser = (force = false) => {
            const now = Date.now();
            const elapsed = now - lastSessionWakeCheckRef.current;
            lastSessionWakeCheckRef.current = now;

            if (!force && elapsed < SESSION_WAKE_REFRESH_AFTER_MS) return;

            silentRestoreAttemptedRef.current = false;
            void refreshUser();
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') maybeRefreshUser();
        };

        const onFocus = () => maybeRefreshUser();
        const onOnline = () => maybeRefreshUser(true);

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
        window.addEventListener('online', onOnline);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
        };
    }, [
        auth.isLoading,
        auth.isAuthenticated,
        auth.activeNavigator,
        auth.user?.access_token,
        auth.user?.expires_at,
        auth.user?.expired,
        auth.user?.refresh_token,
    ]);

    useEffect(() => {
        if (!auth.isAuthenticated || !user || user.email) return;

        const email = getAuthEmail(auth.user);
        if (!email) return;

        const appUser = { ...user, email };
        setUser(appUser);
        persistAccountUser(appUser);
    }, [
        auth.isAuthenticated,
        auth.user?.id_token,
        auth.user?.profile?.email,
        user,
    ]);

    return (
            <UserContext.Provider
            value={{
                user,
                loading,
                error,
                refreshUser,
                updateDisplayName,
            }}
        >
            {children}
            {auth.isAuthenticated && needsDisplayNameOnboarding(user) && (
                <DisplayNameOnboardingModal onSave={updateDisplayName} />
            )}
        </UserContext.Provider>
    );
}

function DisplayNameOnboardingModal({
    onSave,
}: {
    onSave: (displayName: string) => Promise<void>;
}) {
    const [displayName, setDisplayName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const trimmed = displayName.trim();
    const isValid = trimmed.length > 0 && trimmed.length <= MAX_DISPLAY_NAME_LENGTH;

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const submit = async (event?: FormEvent) => {
        event?.preventDefault();
        if (!isValid || saving) return;

        if (containsProfanities(trimmed)) {
            setError('Choose a different display name.');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await onSave(trimmed);
        } catch {
            setError('Unable to save your display name. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flashroom-modal-backdrop" role="presentation">
            <form
                className="flashroom-modal display-name-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="display-name-title"
                onSubmit={submit}
            >
                <h2 id="display-name-title">What would you like your display name to be?</h2>

                <label className="display-name-field">
                    <span>Display name</span>
                    <input
                        ref={inputRef}
                        value={displayName}
                        maxLength={MAX_DISPLAY_NAME_LENGTH}
                        onChange={(event) => {
                            setDisplayName(event.target.value);
                            setError(null);
                        }}
                    />
                </label>

                <div className="display-name-meta">
                    <span>{displayName.length} / {MAX_DISPLAY_NAME_LENGTH}</span>
                </div>

                {error && <p className="display-name-error">{error}</p>}

                <button
                    type="submit"
                    className="display-name-submit"
                    disabled={!isValid || saving}
                >
                    {saving ? 'Saving...' : 'Save display name'}
                </button>
            </form>
        </div>
    );
}

export function useAppUser() {
    const context = useContext(UserContext);

    if (!context) {
        throw new Error(
            'useAppUser must be used inside UserProvider'
        );
    }

    return context;
}
